import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from '@/hooks/ui/use-toast';
import { supabase } from '@/integrations/supabase/client';

interface JobPosting {
  id: string;
  title: string;
  description: string;
  requirements: string;
  location: string;
  employment_type: string;
  experience_level: string;
  business_id: string;
}

const JobApplication: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [jobPosting, setJobPosting] = useState<JobPosting | null>(null);
  const [loadingJobPosting, setLoadingJobPosting] = useState(true);
  const [qrCodeId, setQrCodeId] = useState<string | null>(null);
  
  // Form state
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    workExperience: '',
    education: '',
    skills: '',
    availability: ''
  });

  const qrCode = searchParams.get('code');

  useEffect(() => {
    const fetchJobPosting = async () => {
      if (!qrCode) {
        toast.error("This job application link is invalid or expired.");
        navigate('/');
        return;
      }

      try {
        // Get the QR code record to find the business
        const { data: qrCodeData, error: qrError } = await supabase
          .from('qr_codes')
          .select('id, business_id, type')
          .eq('unique_url_path', qrCode)
          .eq('type', 'job-posting')
          .single();

        if (qrError || !qrCodeData) {
          toast.error("This QR code is not valid for job applications.");
          navigate('/');
          return;
        }

        // Fetch active job postings for this business including form configuration
        const { data: jobPostings, error: jobError } = await supabase
          .from('job_postings')
          .select('*, application_form_config')
          .eq('business_id', qrCodeData.business_id)
          .eq('is_active', true)
          .or('expires_at.is.null,expires_at.gt.now()')
          .order('created_at', { ascending: false })
          .limit(1);

        if (jobError || !jobPostings || jobPostings.length === 0) {
          toast.error("There are currently no active job postings for this business.");
          navigate('/');
          return;
        }

        setJobPosting(jobPostings[0]);
        setQrCodeId(qrCodeData.id);
      } catch (error: any) {
        console.error('Error fetching job posting:', error);
        toast.error("Failed to load job posting information.");
        navigate('/');
      } finally {
        setLoadingJobPosting(false);
      }
    };

    fetchJobPosting();
  }, [qrCode, navigate]);

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!jobPosting) return;

    // Basic validation
    if (!formData.firstName || !formData.lastName || !formData.email) {
      toast.error("Please fill in all required fields (name and email).");
      return;
    }

    setLoading(true);

    try {
      const applicationData = {
        job_posting_id: jobPosting.id,
        business_id: jobPosting.business_id,
        first_name: formData.firstName,
        last_name: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        work_experience: formData.workExperience,
        education: formData.education,
        skills: formData.skills,
        availability: formData.availability,
        application_source: 'qr-code',
        source_tag_id: qrCodeId
      };

      const { error } = await supabase
        .from('job_applications')
        .insert(applicationData);

      if (error) {
        console.error('Error submitting application:', error);
        toast.error("Failed to submit your application. Please try again.");
        return;
      }

      toast.success("Your job application has been submitted successfully. We'll be in touch soon!");

      // Clear form
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        workExperience: '',
        education: '',
        skills: '',
        availability: ''
      });

    } catch (error: any) {
      console.error('Error submitting application:', error);
      toast.error("An unexpected error occurred. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (loadingJobPosting) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading job posting...</p>
        </div>
      </div>
    );
  }

  if (!jobPosting) {
    return null; // This should not happen as we navigate away on error
  }

  return (
    <div className="min-h-screen bg-background p-4">
      <div className="max-w-2xl mx-auto">
        <Card className="mb-6">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3 flex-shrink-0">
            <CardTitle className="text-sm font-medium">Job Application</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-semibold">{jobPosting.title}</h3>
                <p className="text-sm text-muted-foreground">
                  {jobPosting.employment_type} • {jobPosting.experience_level}
                  {jobPosting.location && ` • ${jobPosting.location}`}
                </p>
              </div>
              
              {jobPosting.description && (
                <div>
                  <h4 className="font-medium mb-2">Job Description</h4>
                  <p className="text-sm text-muted-foreground whitespace-pre-wrap">{jobPosting.description}</p>
                </div>
              )}
              
              {jobPosting.requirements && (
                <div>
                  <h4 className="font-medium mb-2">Requirements</h4>
                  <p className="text-sm text-muted-foreground whitespace-pre-wrap">{jobPosting.requirements}</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3 flex-shrink-0">
            <CardTitle className="text-sm font-medium">Apply for This Position</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="firstName">First Name *</Label>
                  <Input
                    id="firstName"
                    value={formData.firstName}
                    onChange={(e) => handleInputChange('firstName', e.target.value)}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="lastName">Last Name *</Label>
                  <Input
                    id="lastName"
                    value={formData.lastName}
                    onChange={(e) => handleInputChange('lastName', e.target.value)}
                    required
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="email">Email Address *</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  required
                />
              </div>

              <div>
                <Label htmlFor="phone">Phone Number</Label>
                <Input
                  id="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                />
              </div>

              <div>
                <Label htmlFor="workExperience">Work Experience</Label>
                <Textarea
                  id="workExperience"
                  value={formData.workExperience}
                  onChange={(e) => handleInputChange('workExperience', e.target.value)}
                  placeholder="Describe your relevant work experience..."
                  rows={4}
                />
              </div>

              <div>
                <Label htmlFor="education">Education</Label>
                <Textarea
                  id="education"
                  value={formData.education}
                  onChange={(e) => handleInputChange('education', e.target.value)}
                  placeholder="Describe your educational background..."
                  rows={3}
                />
              </div>

              <div>
                <Label htmlFor="skills">Skills</Label>
                <Textarea
                  id="skills"
                  value={formData.skills}
                  onChange={(e) => handleInputChange('skills', e.target.value)}
                  placeholder="List your relevant skills..."
                  rows={3}
                />
              </div>

              <div>
                <Label htmlFor="availability">Availability</Label>
                <Textarea
                  id="availability"
                  value={formData.availability}
                  onChange={(e) => handleInputChange('availability', e.target.value)}
                  placeholder="When can you start? What are your preferred hours?"
                  rows={2}
                />
              </div>

              <div className="pt-4">
                <Button type="submit" disabled={loading} className="w-full">
                  {loading ? 'Submitting...' : 'Submit Application'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default JobApplication;